import { Client } from 'urql';
import JSZip from 'jszip';
import { isIOS } from 'react-device-detect';
import { chunk } from 'views/utils/arrayUtils';

export type DownloadFile = {
  fileName: string;
  base64Data: string | null;
};

/** ZIP 内のエントリ名に使えない文字を除去・置換 */
const sanitizeZipEntryName = (name: string): string => {
  const defaultName = 'document';
  const trimmed = name.replace(/[/\\?%*:|"<>]/g, '_').trim();
  return trimmed.length > 0 ? trimmed : defaultName;
};

/**
 * ZIP内のPDFエントリ名を生成する。
 * @param usedNames 同一ZIP内ですでに割り当てたエントリ名の集合。重複時は`名前_1.pdfのように連番を付与する。`
 */
const buildPdfEntryName = (fileName: string, usedNames: Set<string>): string => {
  const base = sanitizeZipEntryName(fileName);
  const withExt = base.toLowerCase().endsWith('.pdf') ? base : `${base}.pdf`;
  let entryName = withExt;
  let n = 1;
  while (usedNames.has(entryName)) {
    const stem = withExt.replace(/\.pdf$/i, '');
    entryName = `${stem}_${n}.pdf`;
    n += 1;
  }
  usedNames.add(entryName);
  return entryName;
};

const downloadDataUrlsSequential = (files: Array<{ fileName: string; base64Data: string }>) => {
  const downloadLink = document.createElement('a');
  // 連続でクリックされると無視されるブラウザ(iphoneのsafariで発生)があるためsetTimeoutで遅延させている
  files.forEach((file, idx) => {
    setTimeout(() => {
      // 現状はPDF専用の想定
      // data:application/pdfの場合にiphoneのsafariが反応しないためoctet-streamにしている
      const source = `data:application/octet-stream;base64,${file.base64Data}`;
      downloadLink.href = source;
      downloadLink.download = file.fileName;
      downloadLink.click();
    }, idx * 50);
  });
};

const downloadZipBlob = (blob: Blob, downloadName: string) => {
  const blobUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = downloadName;
  link.click();
  URL.revokeObjectURL(blobUrl);
};

/**
 * PDF をブラウザで保存する。
 * iOS Safari は同一ジェスチャー内の複数ダウンロードが抑止されるため、複数ファイル時は ZIP にまとめて 1 回だけ落とす。
 */
export const startDownload = async (files: DownloadFile[]): Promise<void> => {
  const validFiles = files.filter(
    (f): f is DownloadFile & { base64Data: string } =>
      typeof f.base64Data === 'string' && f.base64Data.length > 0,
  );
  if (validFiles.length === 0) {
    throw new Error('EMPTY_DOWNLOAD');
  }

  if (validFiles.length === 1) {
    downloadDataUrlsSequential([{ fileName: validFiles[0].fileName, base64Data: validFiles[0].base64Data }]);
    return;
  }

  if (isIOS) {
    const zip = new JSZip();
    const usedNames = new Set<string>();
    for (const file of validFiles) {
      const entryName = buildPdfEntryName(file.fileName, usedNames);
      zip.file(entryName, file.base64Data, {base64: true});
    }
    const blob = await zip.generateAsync({ type: 'blob', compression: 'DEFLATE' });
    const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    downloadZipBlob(blob, `書類_${stamp}.zip`);
    return;
  }

  downloadDataUrlsSequential(
    validFiles.map((f) => ({ fileName: f.fileName, base64Data: f.base64Data })),
  );
};

export const getFiles = async (client: Client, items: { id: string; fileName: string }[]): Promise<DownloadFile[]> => {
  // 選択した数が多い場合に並行でHTTPリクエストするのは負荷が高いかもしれないので5ずつにする
  // (各ファイル内のチャンク取得は getPdfData 内で直列のため、同時リクエスト数は最大5のまま)
  const results = Array<DownloadFile>();
  for (const chunkItems of chunk(items, 5)) {
    const files = await Promise.all(
      chunkItems.map(async (item) => {
        return { fileName: item.fileName, base64Data: (await getPdfData({ PdfId: item.id })) ?? null };
      }),
    );
    results.push(...files);
  }

  return results;
};

export type GetPdfDataParam =
  | { PdfId: string }
  | { ApplySymbolData: { Symbol: string; Amount: number; TransferDate: string } }
  | { ApplicationFileData: { ApplicationID: string; FileType: number } };

type PdfChunkResponse = {
  Code: number;
  Data: string | null;
  HasNext?: boolean;
};

const requestPdfChunk = async (param: GetPdfDataParam, chunkIndex: number): Promise<PdfChunkResponse | null> => {
  const token = JSON.parse(sessionStorage.getItem('auth-store') ?? '');
  const response = await fetch(`${process.env.REACT_APP_API_PDF_URL}/GetPDFData`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: token.state.token,
    },
    body: JSON.stringify({ ...param, ChunkIndex: chunkIndex }),
  });

  if (!response.ok) return null;
  return response.json();
};

/**
 * PDFをチャンク分割で取得して結合する(6MB応答上限の回避)。
 * - サーバはチャンクサイズを3の倍数にしているため、base64文字列の単純連結が全体の正しいbase64になる
 * - HasNext が true の間、次のチャンクを直列で取得(動的PDFはサーバ側でチャンクごとに再生成されるため並列にしない)
 */
export const getPdfData = async (param: GetPdfDataParam): Promise<string | undefined> => {
  // 上限はサーバ異常時の無限ループ保険(250チャンク ≒ 1GB。通常は数チャンクで完了する)
  const maxChunks = 250;
  let base64Data = '';
  let chunkIndex = 0;
  let hasNext;
  do {
    const res = await requestPdfChunk(param, chunkIndex);
    if (!res || res.Code !== 0 || !res.Data) return undefined;
    base64Data += res.Data;
    hasNext = res.HasNext === true;
    chunkIndex += 1;
  } while (hasNext && chunkIndex < maxChunks);

  // 上限到達で打ち切った(=まだ続きがある)場合は、不完全なデータを返さず失敗扱いにする
  return hasNext ? undefined : base64Data;
};
